import styles from '../EntityPicker.module.scss';

import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useMemo } from 'react';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../../tooltip/Tooltip';
import { ERPColumnSelector } from '../components/erp-column-selector/ERPColumnSelector';
import { ListEntityPicker } from '../components/list-entity-picker/ListEntityPicker';
import { usePickerSettings } from '../hooks/use-picker-settings';
import { IPickerProps } from '../types';

export type IContractPickerProps<TMultiple extends boolean> = IPickerProps<
  Contract,
  TMultiple
> & {
  useTimeTrackingQuery?: boolean;
};

export function ContractPicker<TMultiple extends boolean>(
  props: IContractPickerProps<TMultiple>
) {
  const { data, useTimeTrackingQuery, ...rest } = props;

  const queryName = useTimeTrackingQuery
    ? 'getAuftragForTimeTracking'
    : undefined;

  const allFields = useMemo(() => {
    return { ...CONTRACT_FIELDS, ...data };
  }, [data]);

  const { visiblePickerColumnsSetting: contractColumns } = usePickerSettings(
    Entities.contract
  );

  const visibleContractColumns = contractColumns.filter(
    (column) => column.isVisible
  )?.length;

  const gridTemplateColumns =
    visibleContractColumns > 2
      ? '3fr 4fr'
      : visibleContractColumns > 0
      ? '4fr 2fr'
      : '1fr';

  return (
    <ListEntityPicker
      entity={Entities.contract}
      data={allFields}
      filterBy={['note', 'contractNumber', 'number']}
      sortBy="number"
      layout="advanced"
      queryName={queryName}
      {...rest}
      renderItemContent={(contract) => (
        <Stack
          direction="row"
          display="grid"
          gridTemplateColumns={gridTemplateColumns}
          className={clsx({ [styles.greyedStrikeThrough]: contract.isClosed })}
          style={{ width: '100%' }}
          gap={'0.5rem'}
        >
          <Tooltip
            title={`${contract.contractNumber} ${contract.note}`}
            showOnlyOnOverflow
            enterNextDelay={1000}
          >
            <Typography variant="body2" noWrap>
              <span className={styles.secondaryInfo}>
                {contract.contractNumber} |{' '}
              </span>
              {contract.note}
            </Typography>
          </Tooltip>
          <ERPColumnSelector
            item={contract as Contract}
            columnSettings={contractColumns}
          />
        </Stack>
      )}
    />
  );
}

const CONTRACT_FIELDS: Contract<EMode.query> = {
  id: null,
  number: null,
  contractNumber: null,
  contractDate: null,
  note: null,
  isClosed: null,
  positionList: [
    {
      id: null,
      index: null,
      number: null,
      shortText: null,
      longtext: null,
      positionKind: null,
      amount: null,
      unit: null,
      finishedDate: null,
      finishedByUserId: null,
      finishedNote: null,
      article: {
        id: null,
        name: null,
        notdeductable: null,
        isActivity: null,
      },
      isFinished: null,
    },
  ],
  businessPartnerId: null,
  businessPartnerType: null,
  businessPartnerContactCombined: {
    businessPartner: {
      id: null,
      data: {
        customer: {
          id: null,
          name: null,
          number: null,
          website: null,
        },
      },
    },
  },
  project: {
    id: null,
    name: null,
    number: null,
  },
  projectProcess: {
    id: null,
    process: null,
  },
};
